import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  ContextWorkbenchEditorSidebarPanel,
  ContextWorkbenchEditorTab,
  ContextWorkbenchFileId,
  ContextWorkbenchState,
  getContextWorkbenchInitialState,
} from "../../components/context-workbench/redux";
import {
  InitEditorFilePayload,
  UpdateEditorFileContentsPayload,
  initEditorFile,
  updateEditorFileContents,
} from "../../components/context-workbench/redux/reducers/editorFileReducers";
import {
  ResizeWorkbenchRegionPayload,
  ToggleWorkbenchRegionPayload,
  resizeWorkbenchRegion,
  toggleWorkbenchRegion,
} from "../../components/workbench/redux-reducers/regionReducers";
import {
  CloseWorkbenchTabPayload,
  FocusWorkbenchTabPayload,
  NewWorkbenchTabPayload,
  UpdateWorkbenchTabPayload,
  closeWorkbenchTab,
  focusWorkbenchTab,
  newWorkbenchTab,
  openInWorkbenchTab,
  updateWorkbenchTab,
} from "../../components/workbench/redux-reducers/tabReducers";
import {
  UpdateWorkbenchViewportPayload,
  updateWorkbenchViewport,
} from "../../components/workbench/redux-reducers/viewportReducers";
import { RootState } from "../store";

const contextWorkbenchSlice = createSlice({
  name: "contextWorkbench",
  initialState: getContextWorkbenchInitialState(),
  reducers: {
    // --- Editor tabs ---
    newEditorTab(
      state,
      action: PayloadAction<NewWorkbenchTabPayload<ContextWorkbenchEditorTab>>,
    ) {
      newWorkbenchTab(state.editor, action.payload);
    },
    focusEditorTab(state, action: PayloadAction<FocusWorkbenchTabPayload>) {
      focusWorkbenchTab(state.editor, action.payload);
    },
    closeEditorTab(state, action: PayloadAction<CloseWorkbenchTabPayload>) {
      closeWorkbenchTab(state.editor, action.payload);
    },
    updateEditorTab(
      state,
      action: PayloadAction<
        UpdateWorkbenchTabPayload<ContextWorkbenchEditorTab>
      >,
    ) {
      updateWorkbenchTab(state.editor, action.payload);
    },

    // --- Editor viewport ---
    updateEditorViewport(
      state,
      action: PayloadAction<UpdateWorkbenchViewportPayload>,
    ) {
      updateWorkbenchViewport(state.editor, action.payload);
    },

    // --- Editor regions ---
    resizeEditorRegion(
      state,
      action: PayloadAction<ResizeWorkbenchRegionPayload>,
    ) {
      resizeWorkbenchRegion(state.editor.regions, action.payload);
    },
    toggleEditorRegion(
      state,
      action: PayloadAction<ToggleWorkbenchRegionPayload>,
    ) {
      toggleWorkbenchRegion(state.editor.regions, action.payload);
    },

    // --- Editor sidebar ---
    setEditorSidebarPanel(
      state,
      action: PayloadAction<{
        newPanelId: ContextWorkbenchEditorSidebarPanel.Id | null;
      }>,
    ) {
      state.editor.regions.left.isOpen = action.payload.newPanelId != null;
      state.editor.sidebar.panelId = action.payload.newPanelId;
    },

    // --- Editor files ---
    initEditorFile(state, action: PayloadAction<InitEditorFilePayload>) {
      initEditorFile(state, action.payload);
    },
    openEditorFileInTab(
      state,
      action: PayloadAction<{
        fileId: ContextWorkbenchFileId;
        tabType: ContextWorkbenchEditorTab.Type;
      }>,
    ) {
      openInWorkbenchTab<
        ContextWorkbenchState["editor"],
        ContextWorkbenchEditorTab
      >(
        state.editor,
        {
          fileId: action.payload.fileId,
          type: action.payload.tabType,
        },
        {
          matchExistingTab: (tab) =>
            tab.fileId === action.payload.fileId &&
            tab.type === action.payload.tabType,
          getNewTabProperties: (payload) => payload,
        },
      );
    },
    updateEditorFileContents(
      state,
      action: PayloadAction<UpdateEditorFileContentsPayload>,
    ) {
      updateEditorFileContents(state, action.payload);
    },
  },
});

export const contextWorkbenchReducer = contextWorkbenchSlice.reducer;

export const contextWorkbenchActions = contextWorkbenchSlice.actions;

// *** Selectors **************************************************************
export function selectContextWorkbench(
  state: RootState,
): ContextWorkbenchState {
  return state.contextWorkbench;
}
